import {mute, unmute, devicesShortNamesMap} from '@browser'
import LocalVideoStream from './LocalVideoStream'
import LocalAudioStream from './LocalAudioStream'
import Constraints from './Constraints'
import {messenger} from 'communication/src'
import {androidConnector} from 'communication/src/remotes/AndroidConnector'

class LocalStream {
  constructor() {
    const initialState = window.__INITIAL_STATE__
    const {isMicrophoneRecommended, isCameraRecommended} = initialState.app
    this.constraints = new Constraints(
      {
        audio: isMicrophoneRecommended,
        video: isCameraRecommended,
      },
      initialState.app.idealConstraints
    )
    this.video = new LocalVideoStream()
    this.audio = new LocalAudioStream()
    // Browser captures, never Android bridge tracks (see watchEnded)
    this.captures = new WeakSet()
  }

  requestStream = async (type, isRetry = false) => {
    const shortType = devicesShortNamesMap.get(type)
    try {
      return await navigator.mediaDevices.getUserMedia({[shortType]: this.constraints.constraints[shortType]})
    } catch (error) {
      if (isRetry) throw error
      // OverconstrainedError rejects without prompting (typically a stored device that
      // no longer exists) — drop the stored selection and retry once with defaults.
      // Matched by name: Firefox has no OverconstrainedError global constructor.
      if (error.name === 'OverconstrainedError') {
        this.constraints.delete(type)
        this.constraints.init()
        return this.requestStream(type, true)
      }
      // Firefox (notably on Windows) may not have released the previous device yet
      // when switching — a new capture then fails with NotReadableError/AbortError.
      if (error.name === 'NotReadableError' || error.name === 'AbortError') {
        await new Promise(resolve => setTimeout(resolve, 300))
        return this.requestStream(type, true)
      }
      throw error
    }
  }

  addTrack = async (type, options = {}) => {
    const stream = await this.requestStream(type)
    const track = stream.getTracks()[0]

    return this.addExistingTrack(type, track, options)
  }

  // Every capture path (permissions view, recovery, device switch, snapshot resolution change, restart) watches its
  // track, once: its loss (device unplugged, OS privacy switch) reaches PermissionsProcedure's recovery
  watchEnded = track => {
    if (!this.captures.has(track)) {
      this.captures.add(track)
      track.addEventListener('ended', () => {
        messenger.emit('stream:track:end', {name: `${track.kind}_capture`, status: 'denied'})
      })
    }
    return track
  }

  isCapture = track => this.captures.has(track)

  addExistingTrack = (type, track, options = {}) => {
    const shortType = devicesShortNamesMap.get(type)
    track.enabled = !!!options.muted?.[shortType]
    this.watchEnded(track)
    // One track per kind: a capture added while the kind still holds a track (PermissionsView's 'Try again' after the
    // permission-change recovery already re-captured) replaces and stops it, instead of becoming a second P2P sender
    const current = this.getTrackByType(shortType)
    current?.stop()
    const oldTrack = current ?? options.oldTrack
    // Replacing (not adding) emits 'replaceTrack', so Connection moves the sender/producer to the new track
    if (oldTrack) {
      this[shortType].replaceTrack(oldTrack, track)
    } else {
      this[shortType].addTrack(track)
    }
    messenger.emit('stream:track:start', {name: `${track.kind}_capture`, status: 'granted'})
    return track
  }

  addAndroidTrack = async type => {
    const shortType = devicesShortNamesMap.get(type)
    const track = await androidConnector.getTrack(shortType)
    this[shortType].addTrack(track, 'android')
    return track
  }

  getTrack = async (type, options = {}) => {
    const shortType = devicesShortNamesMap.get(type)
    const stream = await this.requestStream(type)
    const track = stream.getTracks()[0]
    track.enabled = !!!options.muted?.[shortType]
    return this.watchEnded(track)
  }

  isAndroidDevice = deviceId => {
    if (androidConnector?.state?.devices && Array.isArray(androidConnector.devices)) {
      return !!androidConnector.devices.find(device => device.deviceId === deviceId)
    }
    return false
  }

  changeDevice = async (device, deviceId, label, options) => {
    this.constraints.update(device, deviceId, label)
    const type = devicesShortNamesMap.get(device)
    this[type].stop()
    if (this.isAndroidDevice(deviceId)) {
      await this.addAndroidTrack(device)
    } else {
      await this.addTrack(device, options)
    }
  }

  replaceDevice = async (device, deviceId, label, options) => {
    this.constraints.update(device, deviceId, label)
    const type = devicesShortNamesMap.get(device)
    const oldTrack = this.getTrackByType(type)
    // No track: the kind is being (or failed to be) re-acquired. Leave the capture to that recovery, which moves the
    // sender; a capture here would be orphaned (replaceTrack(undefined) throws): never sent, never stopped
    if (!oldTrack) return messenger.emit('stream:track:end', {name: `${type}_capture`, status: 'denied'})
    oldTrack.stop()
    let newTrack = null
    if (this.isAndroidDevice(deviceId)) {
      const shortType = devicesShortNamesMap.get(device)
      newTrack = await androidConnector.getTrack(shortType)
    } else {
      newTrack = await this.getTrack(device, options)
    }
    // A device the user turned off stays off, also when the caller read the mute state while a lost kind showed
    // 'not-available' (MeetingView's devicechange switch on a headset unplug)
    if (newTrack && !oldTrack.enabled) newTrack.enabled = false
    this[type].replaceTrack(oldTrack, newTrack)
    this.updateAt = Date.now()
  }

  getTrackByType = type => {
    return this[type].getTrack()
  }

  mute = trackType => {
    if (trackType === 'audio') mute(this.audio.stream.value, trackType)
    if (trackType === 'video') mute(this.video.stream.value, trackType)
  }

  unmute = trackType => {
    if (trackType === 'audio') unmute(this.audio.stream.value, trackType)
    if (trackType === 'video') unmute(this.video.stream.value, trackType)
  }

  hasTorchCapability = async () => {
    const track = this.getTrackByType('video')
    if (track) {
      if ('ImageCapture' in window) {
        const capture = new window.ImageCapture(track)
        const capabilities = await capture.getPhotoCapabilities()
        return capabilities?.fillLightMode?.length > 0
      }
    }
  }

  async toggleTorch() {
    const track = this.getTrackByType('video')
    await track.applyConstraints({
      advanced: [
        {
          torch: !track.getSettings().torch,
        },
      ],
    })
    return track.getSettings().torch
  }

  stop = () => {
    this.video.stop()
    this.audio.stop()
  }
}

const localStream = new LocalStream()
export {localStream}
